/**
 * Tabs Store - 使用 Zustand 管理页签状态
 * 解决 react-activation Context 穿透问题
 */

import { create } from 'zustand';
import { flushSync } from 'react-dom';
import type { TabItem, ProRouteObject } from '../typing';
import type { MenuProps } from 'antd';
import { defaultTabsSettings } from '../defaultSettings';
import { findMatchedRoute, getCacheKeyFromRoute } from '../utils/pathTools';

type AliveController = {
  drop: (cacheKey: string) => void;
  clear?: () => void;
};

export interface TabsState {
  // 状态
  tabs: TabItem[];
  activeKey: string;
  refreshing: boolean;
  maxTabs: number;
  homePath?: string;
  routes: ProRouteObject[];
  aliveController?: AliveController;

  // Actions
  addTab: (tab: Omit<TabItem, 'closable'>) => void;
  removeTab: (key: string) => void;
  setActiveTab: (key: string) => void;
  refreshTab: (key: string) => void;
  closeOtherTabs: (key: string) => void;
  closeLeftTabs: (key: string) => void;
  closeRightTabs: (key: string) => void;
  closeAllTabs: () => void;
  updateTabLabel: (key: string, label: string) => void;
  clearTabs: () => void;
  getContextMenuItems: (key: string) => MenuProps['items'];
  hideRefreshing: () => void;

  // 初始化方法
  setConfig: (config: {
    maxTabs?: number;
    homePath?: string;
    routes?: ProRouteObject[];
    aliveController?: AliveController;
  }) => void;

  // 内部辅助方法
  _navigate?: (to: string, options?: { replace?: boolean; state?: unknown }) => void;
  setNavigate: (navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void) => void;
  _dropCacheForTab: (tab: TabItem) => void;
  _dropCachesForTabs: (tabs: TabItem[]) => void;
}

export const useTabsStore = create<TabsState>((set, get) => ({
  // 初始状态
  tabs: [],
  activeKey: '',
  refreshing: false,
  maxTabs: defaultTabsSettings.maxTabs,
  routes: [],

  // 设置配置
  setConfig: (config) => {
    set({
      maxTabs: config.maxTabs ?? defaultTabsSettings.maxTabs,
      homePath: config.homePath,
      routes: config.routes ?? [],
      aliveController: config.aliveController,
    });
  },

  // 设置 navigate 函数
  setNavigate: (navigate) => {
    set({ _navigate: navigate });
  },

  // 清除缓存 - 单个
  _dropCacheForTab: (tab: TabItem) => {
    const { routes, aliveController } = get();
    const currentRoute = findMatchedRoute(tab.path, routes);
    if (currentRoute?.cache === true && aliveController) {
      const { cacheKey, cacheName } = getCacheKeyFromRoute(
        currentRoute,
        tab.path,
        tab.search || ''
      );
      aliveController.drop(cacheName);
      if (cacheKey !== cacheName) {
        aliveController.drop(cacheKey);
      }
    }
  },

  // 清除缓存 - 批量
  _dropCachesForTabs: (targetTabs: TabItem[]) => {
    targetTabs.forEach(tab => get()._dropCacheForTab(tab));
  },

  /**
   * 添加页签（含 FIFO 策略）
   */
  addTab: (newTab) => {
    if (import.meta.env.DEV) {
      console.log('[TabsStore] addTab called', newTab);
    }

    set((state) => {
      // 检查是否已存在
      const existIndex = state.tabs.findIndex(t => t.key === newTab.key);
      if (existIndex > -1) {
        return { activeKey: newTab.key }; // 已存在，只激活
      }

      // 所有页签默认可关闭
      const tab: TabItem = { ...newTab, closable: true };
      const nextTabs = [...state.tabs, tab];

      // FIFO 策略：超过最大数量时移除最早的可关闭页签
      if (nextTabs.length > state.maxTabs) {
        const removeIndex = nextTabs.findIndex(t => t.closable);
        if (removeIndex > -1) {
          return {
            tabs: nextTabs.filter((_, index) => index !== removeIndex),
            activeKey: newTab.key,
          };
        }
      }

      return {
        tabs: nextTabs,
        activeKey: newTab.key,
      };
    });
  },

  /**
   * 移除页签
   */
  removeTab: (key) => {
    if (import.meta.env.DEV) {
      console.log('[TabsStore] removeTab called', { key });
    }

    let nextActiveTab: TabItem | undefined;
    const { _navigate, _dropCacheForTab } = get();

    // 使用 flushSync 确保状态立即同步更新
    flushSync(() => {
      set((state) => {
        const removeIndex = state.tabs.findIndex(t => t.key === key);
        if (removeIndex === -1) return state;

        // 清除缓存
        const targetTab = state.tabs[removeIndex];
        if (targetTab) {
          _dropCacheForTab(targetTab);
        }

        // 计算删除后的页签列表
        const nextTabs = state.tabs.filter(t => t.key !== key);

        // 如果删除的是当前页签，需要切换到相邻页签
        if (key === state.activeKey && nextTabs.length > 0) {
          nextActiveTab =
            nextTabs[removeIndex] ||
            nextTabs[removeIndex - 1] ||
            nextTabs[0];

          if (import.meta.env.DEV && nextActiveTab) {
            console.log('[TabsStore] Will switch to next tab', {
              from: key,
              to: nextActiveTab.key,
              toPath: nextActiveTab.path,
            });
          }

          return {
            tabs: nextTabs,
            activeKey: nextActiveTab.key,
          };
        }

        return { tabs: nextTabs };
      });
    });

    // 状态已经同步更新，现在可以安全地导航
    if (nextActiveTab && _navigate) {
      _navigate(nextActiveTab.path + (nextActiveTab.search || ''));
    }
  },

  /**
   * 切换页签
   */
  setActiveTab: (key) => {
    const { tabs, _navigate } = get();
    set({ activeKey: key });
    const tab = tabs.find(t => t.key === key);
    if (tab && _navigate) {
      _navigate(tab.path + (tab.search || ''));
    }
  },

  /**
   * 刷新页签
   */
  refreshTab: (key) => {
    const { tabs, routes, aliveController, _navigate } = get();
    const tab = tabs.find(t => t.key === key);
    if (!tab) return;

    // 显示全局 loading
    set({ refreshing: true });

    const fullPath = tab.path + (tab.search || '');

    // 使用统一的路由查找函数
    const currentRoute = findMatchedRoute(tab.path, routes);

    // 如果路由有缓存配置，先清除缓存
    if (currentRoute?.cache === true && aliveController) {
      console.log('[TabsStore] Refreshing cached route:', key);

      const { cacheKey, cacheName } = getCacheKeyFromRoute(
        currentRoute,
        tab.path,
        tab.search || ''
      );

      console.log('[TabsStore] Dropping cache:', { cacheKey, cacheName });

      // 清除缓存（同时清除 name 和 cacheKey）
      aliveController.drop(cacheName);
      if (cacheKey !== cacheName) {
        aliveController.drop(cacheKey);
      }
    }

    // 导航到空白刷新路由，通过 state 传递目标路由信息
    console.log('[TabsStore] Navigating to refresh route with target:', fullPath);
    if (_navigate) {
      _navigate('/___refresh___', {
        state: {
          returnTo: fullPath,
          needDelay: currentRoute?.cache === true,
        },
      });
    }
  },

  /**
   * 关闭其他页签
   */
  closeOtherTabs: (key) => {
    const { _navigate, _dropCachesForTabs } = get();

    let nextActiveTab: TabItem | undefined;

    set((state) => {
      const target = state.tabs.find(t => t.key === key);
      const nextTabs = state.tabs.filter(t => t.key === key || !t.closable);
      const removed = state.tabs.filter(t => !nextTabs.includes(t));
      _dropCachesForTabs(removed);

      nextActiveTab = target;

      return {
        tabs: nextTabs,
        activeKey: nextActiveTab?.key || state.activeKey,
      };
    });

    if (nextActiveTab && _navigate) {
      _navigate(nextActiveTab.path + (nextActiveTab.search || ''));
    }
  },

  /**
   * 关闭左侧页签
   */
  closeLeftTabs: (key) => {
    const { _navigate, _dropCachesForTabs } = get();

    let nextActiveTab: TabItem | undefined;

    set((state) => {
      const index = state.tabs.findIndex(t => t.key === key);
      const nextTabs = state.tabs.filter((t, i) => i >= index || !t.closable);
      const removed = state.tabs.filter(t => !nextTabs.includes(t));
      _dropCachesForTabs(removed);

      const target = nextTabs.find(t => t.key === key);
      if (target && !nextTabs.some(t => t.key === state.activeKey)) {
        nextActiveTab = target;
      }

      return {
        tabs: nextTabs,
        activeKey: nextActiveTab?.key || state.activeKey,
      };
    });

    if (nextActiveTab && _navigate) {
      _navigate(nextActiveTab.path + (nextActiveTab.search || ''));
    }
  },

  /**
   * 关闭右侧页签
   */
  closeRightTabs: (key) => {
    const { _navigate, _dropCachesForTabs } = get();

    let nextActiveTab: TabItem | undefined;

    set((state) => {
      const index = state.tabs.findIndex(t => t.key === key);
      const nextTabs = state.tabs.filter((t, i) => i <= index || !t.closable);
      const removed = state.tabs.filter(t => !nextTabs.includes(t));
      _dropCachesForTabs(removed);

      const target = nextTabs.find(t => t.key === key);
      if (target && !nextTabs.some(t => t.key === state.activeKey)) {
        nextActiveTab = target;
      }

      return {
        tabs: nextTabs,
        activeKey: nextActiveTab?.key || state.activeKey,
      };
    });

    if (nextActiveTab && _navigate) {
      _navigate(nextActiveTab.path + (nextActiveTab.search || ''));
    }
  },

  /**
   * 关闭所有页签
   */
  closeAllTabs: () => {
    const { aliveController, _navigate, _dropCachesForTabs } = get();

    // 清除所有缓存
    if (aliveController) {
      aliveController.clear?.();
    }

    let nextActiveTab: TabItem | undefined;

    set((state) => {
      _dropCachesForTabs(state.tabs);
      const firstTab = state.tabs[0];
      if (firstTab) {
        nextActiveTab = firstTab;
        return {
          tabs: [firstTab],
          activeKey: firstTab.key,
        };
      }
      return {
        tabs: [],
        activeKey: '',
      };
    });

    if (nextActiveTab && _navigate) {
      _navigate(nextActiveTab.path + (nextActiveTab.search || ''));
    }
  },

  /**
   * 更新页签标题
   */
  updateTabLabel: (key, label) => {
    set((state) => ({
      tabs: state.tabs.map(tab =>
        tab.key === key ? { ...tab, label } : tab
      ),
    }));
  },

  /**
   * 清空所有页签（登出时使用）
   */
  clearTabs: () => {
    set({ tabs: [], activeKey: '' });
  },

  /**
   * 获取右键菜单项
   */
  getContextMenuItems: (key) => {
    const { tabs, refreshTab, removeTab, closeOtherTabs, closeLeftTabs, closeRightTabs, closeAllTabs } = get();
    const tab = tabs.find(t => t.key === key);
    if (!tab) return [];

    const onlyOneTab = tabs.length <= 1;

    return [
      {
        key: 'refresh',
        label: '重新載入',
        onClick: () => refreshTab(key),
      },
      { type: 'divider' },
      {
        key: 'close',
        label: '關閉當前標籤',
        disabled: onlyOneTab || !tab.closable,
        onClick: () => removeTab(key),
      },
      {
        key: 'closeOther',
        label: '關閉其他標籤',
        onClick: () => closeOtherTabs(key),
      },
      {
        key: 'closeLeft',
        label: '關閉左側標籤',
        onClick: () => closeLeftTabs(key),
      },
      {
        key: 'closeRight',
        label: '關閉右側標籤',
        onClick: () => closeRightTabs(key),
      },
      { type: 'divider' },
      {
        key: 'closeAll',
        label: '關閉所有標籤',
        onClick: () => closeAllTabs(),
      },
    ] as MenuProps['items'];
  },

  /**
   * 隐藏刷新 loading
   */
  hideRefreshing: () => {
    set({ refreshing: false });
  },
}));
